import React from 'react';
import { Box, Button, Card, FormControl, FormHelperText, IconButton, InputLabel, OutlinedInput, useTheme } from "@mui/material";
import Logo from '../../../components/Logo';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import MaterialIcon from '../../../components/MaterialIcon';
import { useForm } from 'react-hook-form';
import { FISCAL_CODE_OR_VAT_REGEX, PASSWORD_REGEX } from '../../../config/regex';
import { login } from '../../../api/auth';
import { useUserProvider } from '../../../providers/useUserProvider';

type FormValues = {
    username: string;
    password: string;
}

const Login: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { user, setUserId, setUserType } = useUserProvider();
    const { register, handleSubmit, formState, getValues } = useForm<FormValues>({
        defaultValues: {
            username: "",
            password: ""
        },
    });
    const { errors } = formState;
    const navigate = useNavigate();
    const theme = useTheme();

    const [isRequestInProgress, setIsRequestInProgress] = React.useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);

    const onSubmit = async () => {
        const username = getValues("username");
        const password = getValues("password");

        if (isRequestInProgress) {
            return;
        }

        try {
            setIsRequestInProgress(true);

            const response = await login({
                codice_fiscale: username,
                password: password,
                devMode: window.location.hostname.includes("localhost")
            });

            const userId = response.userId;
            const userType = response.userType;

            setUserId(userId);
            setUserType(userType);

            // Naviga alla pagina appropriata basata su userType
            if (userType === 1) {
                navigate("/statistiche");
            } else {
                navigate("/view_shape");
            }
        } catch (error: any) {
            enqueueSnackbar(error.message, { variant: "error" });
        } finally {
            setIsRequestInProgress(false);
        }
    }

    const handleTogglePasswordVisibility = () => {
        setIsPasswordVisible(!isPasswordVisible);
    }

    return (
        <Box
            className="LoginPage"
            alignItems="center"
            width="100svw"
            height="100svh"
            display="flex"
            justifyContent="center"
            bgcolor={theme.palette.primary.main}>
            <Card sx={{ maxWidth: "28rem", margin: "1rem", boxSizing: "border-box", width: "100%" }}>
                <Logo style={{ height: "4.5rem", marginBottom: "2.5rem" }} />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormControl fullWidth variant='outlined' error={!!errors.username}>
                        <InputLabel htmlFor="username">Codice Fiscale o Partita IVA</InputLabel>
                        <OutlinedInput
                            id="username"
                            type="text"
                            fullWidth
                            label="Codice Fiscale o Partita IVA"
                            {...register("username", {
                                required: "Il campo è obbligatorio",
                                pattern: FISCAL_CODE_OR_VAT_REGEX
                            })}
                        />
                        {errors.username && (
                            <FormHelperText>{errors.username.message}</FormHelperText>
                        )}
                    </FormControl>
                    <Box height="1rem" />
                    <FormControl fullWidth variant='outlined' sx={{ marginBottom: "2rem" }} error={!!errors.password}>
                        <InputLabel htmlFor="password">Password</InputLabel>
                        <OutlinedInput
                            id="password"
                            type={isPasswordVisible ? "text" : "password"}
                            fullWidth
                            endAdornment={
                                <IconButton
                                    edge="end"
                                    onClick={handleTogglePasswordVisibility}
                                >
                                    {!isPasswordVisible ? <MaterialIcon icon="visibility" /> : <MaterialIcon icon="visibility_off" />}
                                </IconButton>
                            }
                            label="Password"
                            {...register("password", {
                                required: "Il campo è obbligatorio",

                            })}
                        />
                        {errors.password && (
                            <FormHelperText>{errors.password.message}</FormHelperText>
                        )}
                    </FormControl>
                    <FormControl fullWidth>
                        <Button variant="contained" type="submit" disabled={isRequestInProgress}>Login</Button>
                    </FormControl>
                </form>
                <Box height="1rem" />
                <FormControl fullWidth>
                    <Button variant="text">Hai dimenticato la password?</Button>
                </FormControl>
            </Card>
        </Box >
    );
}

export default Login;
